import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "../../components/Layout";
import ImageBanner from "../../components/ImageBanner";
import { getCourseBySlug } from "./isoLeadAuditorData";
import courseContent from "./isoCourseContent.json";
import {
  ICON_MAP,
  AwardIcon,
  CheckIcon,
  MailIcon,
  BookOpenIcon,
  ArrowRightIcon,
} from "./isoIcons";

/* Courses with a supplied banner image show it, as supplied, in place of the
   text hero. Mapping is exactly as instructed (2026-09-27) — note iso-27701
   was given the ISO 27001 artwork. */
const B = "/assets/banners/";
const COURSE_BANNERS = {
  "sedex-semeta": { src: `${B}SEDEX-SEMETA.png`, width: 1149, height: 377, whole: true },
  "iso-50001": { src: `${B}ISO50001-Certification.png`, width: 1150, height: 641 },
  "iso-45001": { src: `${B}ISO-45001 Certification.png`, width: 1150, height: 641 },
  "iso-27701": { src: `${B}ISO27001-Certification.png`, width: 1149, height: 595 },
  "iso-22000": { src: `${B}ISO22000-Certification.png`, width: 1150, height: 649 },
  "iso-9001": { src: `${B}ISO9001-Certification.png`, width: 1152, height: 615 },
};

const SITE_URL = "https://mbasolutions.co";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const SectionHeading = ({ children, center = false }) => (
  <>
    <h2
      className={`text-3xl md:text-4xl font-bold text-gray-900 mb-4 ${
        center ? "text-center" : ""
      }`}
    >
      {children}
    </h2>
    <div
      className={`w-20 h-1 bg-[#F86624] rounded-full mb-8 ${
        center ? "mx-auto" : ""
      }`}
      aria-hidden="true"
    />
  </>
);

const IsoLeadAuditorDetail = () => {
  const { slug } = useParams();
  const course = getCourseBySlug(slug);
  const content = courseContent[slug];

  if (!course || !course.link || !content) {
    return <Navigate to="/iso-lead-auditor-training" replace />;
  }

  const Icon = ICON_MAP[course.icon] || BookOpenIcon;

  // Shared MBA brand theme (matches site primary #682e92 + orange accent)
  const brandHeroClass =
    "bg-gradient-to-br from-[#1a1a2e] via-[#3d1f5c] to-[var(--bg-primary)]";

  const overviewHeading =
    content.headings.find((h) => h.toLowerCase().includes("overview")) ||
    `${content.title} Overview`;
  const whatHeading =
    content.headings.find((h) => h.toLowerCase().startsWith("what is")) ||
    `What Is ${content.title}?`;
  const featuresHeading =
    content.headings.find((h) => h.toLowerCase().includes("feature")) ||
    `Key Features of ${content.title}`;
  const whyHeading =
    content.headings.find((h) => h.toLowerCase().includes("why choose")) ||
    `Why Choose MBA Solutions for ${course.iso}?`;

  const overviewParagraphs = content.paragraphs.slice(0, 2);
  const remainingParagraphs = content.paragraphs.slice(2, 5);
  const featureCards = content.features.slice(0, 6);
  const primaryList = content.lists[0] || content.focusItems.slice(0, 6);
  const secondaryList = content.lists[1] || [];
  const processList = content.lists.find((list) => list.length >= 5) || secondaryList;

  return (
    <Layout
      title={`MBA Solutions - ${content.title}`}
      description={
        content.subtitle ||
        `${course.iso} ${course.title} training and certification support from MBA Solutions.`
      }
      keywords={`${course.iso}, ${course.title}, ISO Lead Auditor, MBA Solutions, certification`}
      canonical={`${SITE_URL}/iso-lead-auditor-training/${slug}`}
    >
      {COURSE_BANNERS[slug] ? (
        <ImageBanner
          {...COURSE_BANNERS[slug]}
          alt={`${content.title} — MBA Solutions`}
          heading={content.title}
        />
      ) : (
        <section
          className={`relative text-white py-24 px-4 overflow-hidden ${brandHeroClass}`}
        >
          <div className="absolute inset-0 opacity-15" aria-hidden="true">
            <div className="absolute top-20 left-10 w-72 h-72 bg-[#F86624] rounded-full blur-3xl" />
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-[var(--bg-primary)] rounded-full blur-3xl" />
          </div>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.7 }}
            className="relative max-w-5xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
              <Icon size={18} className="text-[#F86624]" aria-hidden="true" />
              <span>{content.badge}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {content.title}
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              {content.subtitle}
            </p>
          </motion.div>
        </section>
      )}

      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading>{overviewHeading}</SectionHeading>
            {overviewParagraphs.map((paragraph) => (
              <p
                key={paragraph.slice(0, 40)}
                className="text-gray-600 text-lg leading-relaxed mb-4"
              >
                {paragraph}
              </p>
            ))}
            {!overviewParagraphs.length && (
              <p className="text-gray-600 text-lg leading-relaxed">
                {course.iso} covers {course.title.toLowerCase()}. MBA Solutions
                provides end-to-end consultancy, training, and audit readiness
                support aligned with international best practices.
              </p>
            )}
          </motion.div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading>{whatHeading}</SectionHeading>
            {remainingParagraphs[0] && (
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {remainingParagraphs[0]}
              </p>
            )}
            {primaryList.length > 0 && (
              <>
                <p className="text-gray-700 font-semibold text-lg mb-4">
                  Key focus areas include:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {primaryList.map((item, index) => (
                    <motion.div
                      key={item}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={fadeUp}
                      transition={{ duration: 0.4, delay: 0.05 * index }}
                      className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                    >
                      <CheckIcon
                        size={22}
                        className="text-[var(--bg-primary)] shrink-0"
                        aria-hidden="true"
                      />
                      <span className="text-gray-700 font-medium">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {featureCards.length > 0 && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <SectionHeading center>{featuresHeading}</SectionHeading>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featureCards.map(([title, desc], index) => (
                <motion.div
                  key={title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.5, delay: 0.08 * index }}
                  className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon size={24} className="text-white" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {title}
                  </h3>
                  <p className="text-gray-600">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {processList.length > 0 && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.6 }}
            >
              <SectionHeading>
                {content.headings.find((h) =>
                  h.toLowerCase().startsWith("how")
                ) || `How to Get Started with ${course.iso}`}
              </SectionHeading>
              <div className="space-y-4">
                {processList.map((step, index) => (
                  <motion.div
                    key={step}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    transition={{ duration: 0.4, delay: 0.05 * index }}
                    className="flex items-start gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--bg-primary)] text-white text-sm font-bold">
                      {index + 1}
                    </span>
                    <span className="text-gray-700 pt-1">{step}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading>{whyHeading}</SectionHeading>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              MBA Solutions Pvt. Ltd. provides end-to-end support for{" "}
              {course.iso} — including gap analysis, documentation, training,
              internal audits, and certification-body readiness — so your
              organization can achieve compliance efficiently and confidently.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {[
                "Experienced ISO consultants",
                "Industry-aligned documentation",
                "Practical audit preparation",
                "Training for teams and auditors",
                "Support across multiple sectors",
                "Clear roadmap to certification",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <ArrowRightIcon
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`rounded-3xl shadow-2xl p-10 text-center text-white ${brandHeroClass}`}
          >
            <div className="flex justify-center mb-5">
              <AwardIcon size={36} className="text-[#F86624]" aria-hidden="true" />
            </div>
            <h3 className="text-2xl font-bold mb-3">
              Ready to Start Your {course.iso} Journey?
            </h3>
            <p className="text-gray-200 mb-6 max-w-2xl mx-auto text-sm">
              Talk to MBA Solutions about training, consultancy, and
              certification support tailored to your organization.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[#F86624] hover:bg-[#e55a1a] text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
              >
                <MailIcon size={18} aria-hidden="true" /> Contact Us
              </Link>
              <Link
                to="/iso-lead-auditor-training"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/20 transition-all"
              >
                All Programs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default IsoLeadAuditorDetail;
